import { all, put, race, take, select, takeEvery, takeLatest } from 'redux-saga/effects'
import { SUCCESS, FAILED } from 'crom-api-constants'
import _ from 'lodash'

import { getToken, getLocale } from '@global/selectors'

import {
    CONTRACTS_TABLE_CHANGE,
    CONTRACTS_TABLE_CHANGE_REQUEST,
    CONTRACTS_TABLE_LOADING_TRUE,
    CONTRACTS_TABLE_LOADING_FALSE,
    CONTRACTS_TABLE_DATA_RECEIVED,
} from './constants'

import {
    contractsTableDataReceived,
    contractsTableChangeRequest,
    contractsTableLoadingTrue,
    contractsTableLoadingFalse,
} from './actions'

import participantsSaga from './components/Table/components/Participants/saga'

import { BLOCK_DATA_LOADED } from '../../../../../../../components/Workspace/components/HocBlock/constants'
import { blockSaveCustomData } from '../../../../../../../components/Workspace/components/HocBlock/actions'
import config from '../../../../../../../../../config'

const CONTRACTS_TABLE_CHANGE_REQUEST_SUCCESS = (id) => `${CONTRACTS_TABLE_CHANGE_REQUEST(id)}_${SUCCESS}`
const CONTRACTS_TABLE_CHANGE_REQUEST_FAILED = (id) => `${CONTRACTS_TABLE_CHANGE_REQUEST(id)}_${FAILED}`

function* onDataLoaded(_action) {
    const blockKey = _.get(_action, 'data.blockKey', null)
    if (blockKey !== config.modules.report.blocks.contracts.key) return null

    const module = _.get(_action, 'data.module', null)
    const id = _.get(_action, 'data.id', null)
    if (!module || !id || !blockKey) return null

    const sorted = _.reduce(
        _.get(_action, 'data.data[1].query.sort.sortedColumns', []),
        (acc, val) => [
            ...acc,
            {
                id: _.get(val, 'columm'), // TYPO FROM BACKEND
                desc: _.get(val, 'direction') === 'desc',
            },
        ],
        []
    )

    yield put(
        blockSaveCustomData(module, id, blockKey, {
            isLoading: false,
            data: _.get(_action, 'data.data[1].rows', []),
            pagination: _.get(_action, 'data.data[1].query.pagination', {}),
            filter: _.get(_action, 'data.data[1].query.filter', {}),
            sorted,
        })
    )

    return null
}

function* contractsTableSaga(action) {
    const module = _.get(action, 'data.module', null)
    const blockKey = _.get(action, 'data.blockKey', null)
    const id = _.get(action, 'data.id', null)
    const level = _.get(action, 'data.level', null)
    const customData = _.get(action, 'data.customData', null)
    const queryParams = _.get(action, 'data.queryParams', null)

    const locale = yield select(getLocale)
    const token = yield select(getToken, id, module)

    const lang = config.languages[locale].requestKey

    if (!module || !blockKey || !id || !level) return null

    yield put(contractsTableLoadingTrue(module, id, blockKey, { ...customData, isLoading: true }))

    const filters = _.reduce(
        _.get(queryParams, 'filtered'),
        (acc, filter) => ({
            ...acc,
            [`${filter.id}[]`]: filter.value,
        }),
        {}
    )

    const qp = {
        page: _.get(queryParams, 'pagination.page'),
        rows: _.get(queryParams, 'pagination.rowsPerPage'),
        'sort[]': _.map(_.get(queryParams, 'sorted', []), (val) => `${_.get(val, 'id')}${val.desc ? ':desc' : ':asc'}`),
        ...filters,
    }

    yield put(contractsTableChangeRequest(module, blockKey, id, level, lang, token, qp))

    const response = yield race({
        success: take(CONTRACTS_TABLE_CHANGE_REQUEST_SUCCESS(id)),
        failed: take(CONTRACTS_TABLE_CHANGE_REQUEST_FAILED(id)),
    })

    if (!response.failed) {
        const data = _.map(_.get(response, 'success.payload.response.body.rows'), (row) => ({ ...row }))
        const query = _.get(response, 'success.payload.response.body.query', [])
        const pagination = _.get(query, 'pagination', null)
        const filter = _.get(query, 'filter', null)
        const sorted = _.reduce(
            _.get(query, 'sort.sortedColumns'),
            (acc, val) => [
                ...acc,
                {
                    id: _.get(val, 'columm'), // TYPO FROM BACKEND
                    desc: _.get(val, 'direction') === 'desc',
                },
            ],
            []
        )

        const newCustomData = {
            ...customData,
            data,
            pagination,
            sorted,
            filter,
            isLoading: false,
        }

        yield put(contractsTableDataReceived(module, id, blockKey, newCustomData))
    } else {
        yield put(contractsTableLoadingFalse(module, id, blockKey, { ...customData, isLoading: false }))
    }

    return null
}

function* contractsChangeCustomData(action) {
    const blockKey = _.get(action, 'data.blockKey', null)
    if (blockKey !== config.modules.report.blocks.contracts.key) return null

    const module = _.get(action, 'data.module', null)
    const id = _.get(action, 'data.id', null)
    if (!module || !id || !blockKey) return null

    const customData = _.get(action, 'data.customData', null)

    yield put(blockSaveCustomData(module, id, blockKey, customData))

    return null
}

function* contractsCallSaga() {
    yield all([
        takeEvery(BLOCK_DATA_LOADED, onDataLoaded),
        takeEvery(CONTRACTS_TABLE_LOADING_TRUE, contractsChangeCustomData),
        takeEvery(CONTRACTS_TABLE_LOADING_FALSE, contractsChangeCustomData),
        takeEvery(CONTRACTS_TABLE_DATA_RECEIVED, contractsChangeCustomData),
        takeLatest(CONTRACTS_TABLE_CHANGE, contractsTableSaga),
    ])
}

export default function* contractsSaga() {
    yield all([contractsCallSaga(), participantsSaga()])
}
